import React, {useEffect, useCallback, useMemo, useState, useRef} from "react";
import {Message} from "../types/types";
import {formatBotResponseHTML} from "../helpers/formatBotResponseHTML";

type Props = {
  messages: Message[];
  onSubmit: (message?: string) => void;
  bottomRef: React.RefObject<HTMLDivElement | null>;
  isLoading: boolean;
  isThinking: boolean;
  setInputValue: (val: string) => void;
};

const ChatHistory: React.FC<Props> = ({
  messages,
  bottomRef,
  isLoading,
  isThinking,
  onSubmit,
  setInputValue,
}) => {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const lastUserRef = useRef<HTMLDivElement | null>(null);

  const [innerHeight, setInnerHeight] = useState<number>(0);

  useEffect(() => {
    if (lastUserRef.current) {
      lastUserRef.current.scrollIntoView({behavior: "smooth", block: "start"});
    }
  }, [messages]);

  useEffect(() => {
    if (containerRef.current) {
      const height = containerRef.current.scrollHeight;
      setInnerHeight(height);
    }
  }, [messages, isLoading]);

  const handleClick = useCallback(
    (e: React.MouseEvent<HTMLDivElement>) => {
      const target = e.target as HTMLElement;
      if (target.tagName.toLowerCase() === "button") {
        const text = target.textContent || "";
        setInputValue(text);
        onSubmit(text);
      }
    },
    [setInputValue, onSubmit]
  );

  const renderedMessages = useMemo(() => {
    let combinedBotMessages: string[] = [];
    const result: React.ReactNode[] = [];

    messages.forEach((msg, idx) => {
      if (msg.sender === "user") {
        // flush bot messages before user message
        if (combinedBotMessages.length > 0) {
          const botKey = `bot-${idx}-group`;
          result.push(
            <div key={botKey} className="iris-chat-bubble iris-bot">
              <span
                dangerouslySetInnerHTML={{
                  __html: formatBotResponseHTML(combinedBotMessages.join("")),
                }}
              />
            </div>
          );
          combinedBotMessages = [];
        }

        const isLastUser =
          idx === messages.length - 1 ||
          messages.slice(idx + 1).every((m) => m.sender !== "user");

        result.push(
          <div
            key={`user-${idx}`}
            className="iris-chat-bubble iris-user"
            ref={isLastUser ? lastUserRef : null}>
            {msg.message}
          </div>
        );
      } else if (msg.sender === "bot" || msg.sender === "assistant") {
        combinedBotMessages.push(msg.message);
      }
    });

    // flush remaining bot messages
    if (combinedBotMessages.length > 0) {
      const lastBotKey = `bot-${messages.length}-final`;
      result.push(
        <div key={lastBotKey} className="iris-chat-bubble iris-bot">
          <span
            dangerouslySetInnerHTML={{
              __html: formatBotResponseHTML(combinedBotMessages.join("")),
            }}
          />
        </div>
      );
    }

    return result;
  }, [messages]);

  return (
    <div
      ref={containerRef}
      className={`iris-chat-history ${
        messages.length === 0 ? "" : "iris-chat-history-active"
      }`}
      style={{
        overflowY: innerHeight > 250 ? "auto" : "hidden",
      }}
      onClick={handleClick}>
      {renderedMessages}
      {isThinking ? (
        <div className="iris-chat-bubble iris-bot typing-indicator thinking-indicator">
          <span className="thinking-label">Thinking</span>
          <span className="dot" />
          <span className="dot" />
          <span className="dot" />
        </div>
      ) : (
        isLoading && (
          <div className="iris-chat-bubble iris-bot typing-indicator">
            <span className="dot" />
            <span className="dot" />
            <span className="dot" />
          </div>
        )
      )}
    </div>
  );
};

export default ChatHistory;
